import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'

import {systemAlert} from '@web/actions/application'
import {
    AddressBookRequestFailMessage,
    AddressBookRequestFailMessageGetter,
    DefaultAddressBookRequestFailMessage,
    isAddressBookRequestFailMessageGetterKey,
    isAddressBookRequestFailMessageKey,
} from '@web/core/constants'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {BaseAddressInfo} from '@web/slices/deliveryInfo/slice'

import addressBookApi from './apis/addressBook'
import {RecentAddress, RegisteredAddress} from './interfaces'

const convertYNTFtoBoolean = (value: string) => ['Y', 'T'].some((x) => x === value)

const convertAddress = (addressInfo: RegisteredAddress | RecentAddress, isRegistered: boolean) => {
    return {
        hash: addressInfo.hash,
        addressName: decode(addressInfo.addressName),
        receiverName: decode(addressInfo.receiverName),
        zipCode: addressInfo.zipCode,
        buildingManagementNo: addressInfo.location?.buildId ?? null,
        locationX: addressInfo.location?.x ?? null,
        locationY: addressInfo.location?.y ?? null,
        baseAddress: decode(addressInfo.baseAddress),
        detailAddress: decode(addressInfo.detailAddress ?? ''),
        isRoadNameAddress: convertYNTFtoBoolean(addressInfo.roadNameYn),
        telNo1: addressInfo.telNo1,
        telNo2: addressInfo.telNo2 ?? null,
        locationType: addressInfo.locationCode,
        locationDescription: addressInfo.locationDescription,
        entryMethodType: addressInfo.entranceCode,
        entryDescription: addressInfo.entranceDescription,
        isPrimaryAddress: 'primaryYn' in addressInfo ? convertYNTFtoBoolean(addressInfo.primaryYn) : false,
        isRegistered,
        isDeleted: false,
    }
}

/** 배송지 리스트 조회 액션 */
export const getAddressBook = createAsyncThunk('deliveryManager/getAddressBook', async (_, {dispatch}) => {
    try {
        const {addressBooks = [], recentlyUseds = [], isAddible} = await addressBookApi.getAddressBook()
        const registeredAddressBook = addressBooks.map((address) => convertAddress(address, true))
        const recentAddressBook = recentlyUseds.map((address) => convertAddress(address, false))

        return {list: [...registeredAddressBook, ...recentAddressBook], isAddible}
    } catch {
        dispatch(systemAlert({type: 'ALERT', message: '일시적인 오류입니다. 다시 시도해주세요'}))

        return {list: [], isAddible: false}
    }
})

/** 주문서 > 주소오류배너로 진입 시 주소변경액션 */
export const saveAsDefaultAddress = createAsyncThunk<
    BaseAddressInfo,
    BaseAddressInfo,
    {
        rejectValue: string
    }
>('deliveryManager/saveAsDefaultAddress', async (address, {rejectWithValue}) => {
    try {
        const {hash} = await addressBookApi.addAddress({
            address: {
                ...address,
                setAsPrimaryAddress: true,
                detailAddress: address.detailAddress ?? '',
                telNo2: address.telNo2 ?? '',
            },
        })

        /**
         * 최근배송지 등록 완료 후 등록 완료된 최근배송지 삭제 실패 케이스에는
         * 별도 처리 없이 pass하기 위해 catch가 빈 try - catch문으로 처리합니다
         */
        try {
            await addressBookApi.deleteAddress({hash: address.hash, type: 'recent'})
        } catch {}

        return {
            ...address,
            hash,
        }
    } catch (e) {
        if (isApiError(e)) {
            const code = e.response?.data?.result?.code
            const usingServices = e.response?.data?.result?.usingServices

            if (code && typeof code === 'string') {
                let message = DefaultAddressBookRequestFailMessage
                if (isAddressBookRequestFailMessageKey(code)) {
                    message = AddressBookRequestFailMessage[code]
                } else if (isAddressBookRequestFailMessageGetterKey(code)) {
                    message = AddressBookRequestFailMessageGetter[code]({method: 'post', usingServices})
                }

                return rejectWithValue(message)
            }
        }
        return rejectWithValue('배송지 저장에 실패했습니다.')
    }
})

/** 배송지 삭제 액션 */
export const deleteAddress = createAsyncThunk<
    {hash: string},
    {hash: string; isRegistered: boolean},
    {rejectValue: {message: string}}
>('deliveryManager/deleteAddress', async ({hash, isRegistered}, {rejectWithValue}) => {
    try {
        await addressBookApi.deleteAddress({hash, type: isRegistered ? 'registered' : 'recent'})
        return {hash}
    } catch (e) {
        if (isApiError(e)) {
            const code = e.response?.data?.result?.code
            const usingServices = e.response?.data?.result?.usingServices

            if (code && typeof code === 'string') {
                let message = DefaultAddressBookRequestFailMessage

                if (isAddressBookRequestFailMessageKey(code)) {
                    message = AddressBookRequestFailMessage[code]
                } else if (isAddressBookRequestFailMessageGetterKey(code)) {
                    message = AddressBookRequestFailMessageGetter[code]({method: 'delete', usingServices})
                }

                return rejectWithValue(new Error(message))
            }
        }
        return rejectWithValue(new Error(DefaultAddressBookRequestFailMessage))
    }
})
